import type { CSSProperties } from 'react'

export interface SkeletonProps {
  className?: string
  variant?: 'text' | 'circle' | 'rect'
  width?: string | number
  height?: string | number
  count?: number
}

/** Loading = Skeleton (CRM kit rule 5). */
export function Skeleton({ className = '', variant = 'rect', width, height, count = 1 }: SkeletonProps) {
  const shape = variant === 'text' ? 'h-3 w-full' : variant === 'circle' ? 'rounded-full' : ''
  const style: CSSProperties = {
    ...(width !== undefined ? { width: typeof width === 'number' ? `${width}px` : width } : {}),
    ...(height !== undefined ? { height: typeof height === 'number' ? `${height}px` : height } : {}),
  }
  const one = (i: number) => (
    <div key={i} className={`skeleton ${shape} ${className}`} style={style} aria-hidden="true" />
  )
  if (count > 1) return <div className="space-y-2">{Array.from({ length: count }).map((_, i) => one(i))}</div>
  return one(0)
}

export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="card-flush" role="status" aria-label="Loading">
      <div className="flex gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} variant="text" width={`${100 / cols}%`} height={10} />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="flex items-center gap-3 border-b border-slate-100 px-3 py-2.5 last:border-0 dark:border-slate-800"
        >
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} variant="text" width={`${100 / cols}%`} height={12} />
          ))}
        </div>
      ))}
    </div>
  )
}

export function CardSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="card space-y-2" role="status" aria-label="Loading">
      <div className="flex items-center gap-3">
        <Skeleton variant="circle" width={32} height={32} />
        <div className="flex-1 space-y-1">
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="30%" height={10} />
        </div>
      </div>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} variant="text" width={`${90 - i * 15}%`} />
      ))}
    </div>
  )
}

export function StatSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" role="status" aria-label="Loading">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="card flex items-center gap-3">
          <Skeleton width={32} height={32} />
          <div className="flex-1 space-y-1">
            <Skeleton variant="text" width="60%" height={10} />
            <Skeleton variant="text" width="40%" height={16} />
          </div>
        </div>
      ))}
    </div>
  )
}
